// @owners { team: patients-team }
import { Button, Column, Row, Toast, ToastContext } from '@alto/design-system';
import { useGetDeliveryMethodsByShipmentIDs } from '@alto/features';
// eslint-disable-next-line @alto/no-pocky-import
import { DeprecatedModalBody, Modal } from '@alto/pocky';
import { type ShipmentTip } from '@alto/scriptdash/alto/orders/types/v1/shipment_tip';
import {
  type CreateShipmentTipRequest,
  type UpdateShipmentTipRequest,
} from '@alto/scriptdash/alto/patients/v3/courier_tips/wunderbar/v1/shipment_tips_endpoint';
import React, { type ElementRef, useContext, useEffect, useState } from 'react';
import { nextModalStep } from '~shared/actions/modal';
import { viewNotice } from '~shared/actions/notices';
import { rateShipmentDismissed } from '~shared/actions/rateShipment';
// eslint-disable-next-line import/no-deprecated
import { type SurveyResponsePayload, submitSurveyResponse } from '~shared/actions/surveyResponses';
import { DEFAULT_ERROR_ALERT_MESSAGE } from '~shared/constants';
import { getIsTippableForShipmentID } from '~shared/features/checkout/selectors/getIsTippable';
import { SOURCES } from '~shared/features/courier-tips/AnalyticsProps';
// eslint-disable-next-line import/no-deprecated
import { createShipmentTip, fetchShipmentTipsForUser, updateShipmentTip } from '~shared/features/courier-tips/actions';
import { COURIER_TIP_COPY, DEFAULT_TIP_AMOUNT } from '~shared/features/courier-tips/constants';
import { canSubmitCourierTip, canTipAfterRating } from '~shared/features/courier-tips/helpers';
import { getShipmentTipForShipmentID } from '~shared/features/courier-tips/selectors';
import { POST_DELIVERY_MODAL_NOTICE } from '~shared/features/notices/selectors/getNotices';
import { NO_RATING_ERROR_MESSAGE, createSurveyResponseDataPayload } from '~shared/features/rate-shipment/helpers';
import getPendingRating from '~shared/features/rate-shipment/selectors/getPendingRating';
import { getShipmentAddress, getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { formatDollars } from '~shared/helpers/currency';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getModalCurrentStep from '~shared/selectors/modal/getModalCurrentStep';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import EditOrAddTip from '~web/features/courier-tips/components/EditOrAddTip';
import { RateShipment } from '~web/features/rate-shipment/RateShipment';

type Props = {
  readonly onClose: () => void;
};

export const PostDeliveryModal = ({ onClose }: Props) => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const { addToast } = useContext(ToastContext);

  const pendingRating = useSelectorShared(getPendingRating);
  const uuid = pendingRating?.uuid;
  const shipmentID = pendingRating?.shipment_id;

  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID }));
  const address = useSelectorShared((state) => getShipmentAddress(state, { shipmentID }));
  const shipmentTip = useSelectorShared((state) => getShipmentTipForShipmentID(state, { shipmentID }));

  const [rating, setRating] = useState(0);
  const [freeformReason, setFreeformReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [tipAmount, setTipAmount] = useState(shipmentTip?.amount ?? DEFAULT_TIP_AMOUNT);
  const [tipPaymentMethodID, setTipPaymentMethodID] = useState(shipmentTip?.payment_method_id);

  const currentUserID = useSelectorShared(getCurrentUserID);
  const currentStep = useSelectorShared(getModalCurrentStep);
  const tipAnalyticsProps = {
    shipment_id: shipmentID,
    source: SOURCES.POST_DELIVERY,
    facility_id: address?.dispensing_facility_id,
  };

  const { deliveryMethodsByShipmentID } = useGetDeliveryMethodsByShipmentIDs(shipmentID ? [shipmentID] : []);
  const deliveryMethod = shipmentID ? deliveryMethodsByShipmentID[shipmentID] : undefined;
  const isTippable = useSelectorShared((state) => getIsTippableForShipmentID(state, { shipmentID, deliveryMethod }));

  useEffect(() => {
    trackEvent({
      event: EVENTS.RATE_SHIPMENT__EXPERIENCE_VIEWED,
      params: {
        view: 'modal',
        uuid,
      },
    });
  }, [trackEvent, uuid]);

  // first star
  const firstFocusableElement: ElementRef<any> = React.createRef();
  useEffect(() => {
    if (!currentUserID) return;
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchShipmentTipsForUser(currentUserID));

    // pretty hacky but need to do it this way for 2 reasons:
    // 1. setting ref on the Star SVG results in an exception
    // 2. focusing the firstFocusableElement (StarWrapper) results in a focus ring visible on mount
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    firstFocusableElement.current?.children[0]?.focus(); // intentionally leave array empty so that this only executes once on mount

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRatingChangeWithAnalytics = (rating: number) => {
    trackEvent({
      event: EVENTS.RATE_SHIPMENT__RATING_CLICKED,
      params: {
        view: 'modal',
        rating,
        uuid,
      },
    });
    setErrorMessage('');
    setRating(rating);
  };

  const handleSubmitRating = () => {
    if (rating === 0) {
      setErrorMessage(NO_RATING_ERROR_MESSAGE);
    } else {
      const isEligibleToTip = canTipAfterRating({
        isTippable,
        rating,
        shipment,
        shipmentTip,
      });

      if (isEligibleToTip) {
        dispatch(nextModalStep('POST_DELIVERY_MODAL'));
      } else {
        onClose();
      }

      const surveyResponse: SurveyResponsePayload = {
        name: 'rate_shipment',
        data: createSurveyResponseDataPayload(rating, freeformReason),
        survey_uuid: uuid ?? undefined,
        version: 2,
      };
      // eslint-disable-next-line import/no-deprecated
      dispatch(submitSurveyResponse(surveyResponse));
      dispatch(viewNotice(POST_DELIVERY_MODAL_NOTICE));
      addToast(<Toast>Thank you for your rating!</Toast>);

      trackEvent({
        event: EVENTS.RATE_SHIPMENT__SUBMIT_CLICKED,
        params: { uuid, view: 'modal' },
      });
    }
  };

  const handleCourierTipError = (e: unknown) => {
    addToast(<Toast variant="error">{(e as Error)?.message || DEFAULT_ERROR_ALERT_MESSAGE}</Toast>);
    onClose();
  };

  const handleCourierTipSuccess = (tipAmount: number | null | undefined) => {
    if (typeof tipAmount === 'number' && tipAmount >= 0) {
      addToast(<Toast>{COURIER_TIP_COPY.TOAST_SUCCESSFUL_UPDATE}</Toast>);
    }
    onClose();
  };

  const handleSubmitCourierTip = async () => {
    if (!tipPaymentMethodID) return;

    trackEvent({
      event: EVENTS.COURIER_TIPS__POST_DELIVERY_TIP_SUBMITTED,
      params: {
        ...tipAnalyticsProps,
        payment_method_id: tipPaymentMethodID,
        tip_value: formatDollars(tipAmount, false),
        view: undefined,
      },
    });
    if (shipmentTip) {
      const updatedShipmentTip: ShipmentTip = {
        ...shipmentTip,
        payment_method_id: tipPaymentMethodID,
        amount: tipAmount,
      };
      const request: UpdateShipmentTipRequest = {
        id: shipmentTip.id,
        shipment_tip: updatedShipmentTip,
      };

      try {
        // eslint-disable-next-line import/no-deprecated
        const updatedTip = await dispatch(updateShipmentTip(request));
        handleCourierTipSuccess(updatedTip?.amount);
      } catch (e) {
        handleCourierTipError(e);
      }
    } else if (currentUserID && shipmentID) {
      const request: CreateShipmentTipRequest = {
        user_id: currentUserID,
        shipment_id: shipmentID,
        payment_method_id: tipPaymentMethodID,
        amount: tipAmount,
      };
      try {
        // eslint-disable-next-line import/no-deprecated
        const savedTip = await dispatch(createShipmentTip(request));
        handleCourierTipSuccess(savedTip?.amount);
      } catch (e) {
        handleCourierTipError(e);
      }
    }
  };

  const handleDismissRating = () => {
    onClose();
    dispatch(rateShipmentDismissed());
    dispatch(viewNotice(POST_DELIVERY_MODAL_NOTICE));
    trackEvent({
      event: EVENTS.RATE_SHIPMENT__DISMISS_CLICKED,
      params: {
        view: 'modal',
        uuid,
      },
    });
  };

  const handleDismissTip = () => {
    trackEvent({
      event: EVENTS.COURIER_TIPS__POST_DELIVERY_TIP_DISMISSED,
      params: {
        view: undefined,
        ...tipAnalyticsProps,
      },
    });
    onClose();
  };

  // TODO: add transition between these pages
  const pageOptions = [
    {
      component: (
        <RateShipment
          rating={rating}
          onRatingChange={onRatingChangeWithAnalytics}
          freeformReason={freeformReason}
          onFreeformReasonChange={setFreeformReason}
          errorMessage={errorMessage}
          firstFocusableElementRef={firstFocusableElement}
          isModal
        />
      ),
      onDismiss: handleDismissRating,
      onSubmit: handleSubmitRating,
      disabled: false,
    },
    {
      component: (
        <EditOrAddTip
          editTipAmount={setTipAmount}
          editTipPaymentMethodID={setTipPaymentMethodID}
          tipAmount={tipAmount}
          tipPaymentMethodID={tipPaymentMethodID}
          analyticsProps={{
            shipment_id: shipmentID,
            source: SOURCES.POST_DELIVERY,
            facility_id: address?.dispensing_facility_id,
          }}
        />
      ),
      onDismiss: handleDismissTip,
      onSubmit: handleSubmitCourierTip,
      disabled: !canSubmitCourierTip(tipAmount, tipPaymentMethodID),
    },
  ];

  const { component, disabled, onDismiss, onSubmit } = pageOptions[currentStep];

  return (
    <Modal
      dismissOnOutsideClick={false}
      onClose={onDismiss}
      showDismiss
    >
      <DeprecatedModalBody>
        <Column gap="MD">
          {component}
          <Row
            right
            gap="SM"
          >
            <Button
              label="Dismiss"
              accessibilityLabel="Press to close modal"
              onPress={onDismiss}
              type="secondary"
            />
            <Button
              label="Submit"
              accessibilityLabel="Press to submit"
              disabled={disabled}
              onPress={onSubmit}
            />
          </Row>
        </Column>
      </DeprecatedModalBody>
    </Modal>
  );
};
